import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Customers() {
  const [data, setData] = useState(null);
  const load = () =>
    api
      .get("/customers")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/customers/${id}`, body);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ei-navy" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <Users className="w-6 h-6 text-ei-golddark" />
        <h1 className="text-2xl font-bold text-ei-navy">Trade accounts</h1>
        {data.pending > 0 && (
          <span className="text-xs font-semibold bg-ei-gold/20 text-ei-golddark px-3 py-1 rounded-full" data-testid="customers-pending">
            {data.pending} pending approval
          </span>
        )}
      </div>

      <div className="mt-8" data-testid="customers-list">
        {data.customers.length === 0 ? (
          <p className="text-sm text-ei-graphite/60" data-testid="customers-empty">No trade accounts yet.</p>
        ) : (
          <div className="space-y-3">
            {data.customers.map((c) => (
              <div
                key={c.id}
                className="border border-ei-mist rounded-md p-4 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                data-testid={`customer-${c.id}`}
              >
                <div>
                  <div className="font-semibold text-ei-navy">
                    {c.name} <span className="font-normal text-ei-graphite/60">· {c.company}</span>
                  </div>
                  <div className="mt-1 text-xs text-ei-graphite/60">{c.email} · {c.phone}</div>
                  <div className="mt-1 flex gap-2">
                    <span
                      data-testid={`customer-status-${c.id}`}
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                    >
                      {c.approved ? "Approved" : "Pending"}
                    </span>
                    {!c.active && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Disabled</span>
                    )}
                    {c.reset_email_error && (
                      <span
                        data-testid={`customer-reset-email-failed-${c.id}`}
                        title={c.reset_email_error.detail}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                      >
                        Password email failed
                      </span>
                    )}
                    {c.spam_suspected && (
                      <span
                        data-testid={`customer-autofill-flag-${c.id}`}
                        title="The hidden anti-spam field was filled when this account registered — usually browser autofill, occasionally a bot."
                        className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                      >
                        Check — autofill flag
                      </span>
                    )}
                  </div>
                  {c.reset_email_error && (
                    <div className="mt-1 text-xs text-red-700" data-testid={`customer-reset-email-detail-${c.id}`}>
                      Last reset email could not be sent ({c.reset_email_error.at.slice(0, 16).replace("T", " ")}): {c.reset_email_error.detail}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {!c.approved ? (
                    <button
                      onClick={() => patch(c.id, { approved: true })}
                      data-testid={`customer-approve-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 border border-green-200 px-3 py-2 rounded-sm hover:bg-green-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Approve
                    </button>
                  ) : (
                    <button
                      onClick={() => { if (window.confirm("Revoke this account's access? They won't be able to sign in until re-approved.")) patch(c.id, { approved: false }); }}
                      data-testid={`customer-revoke-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 border border-amber-200 px-3 py-2 rounded-sm hover:bg-amber-50"
                    >
                      <XCircle className="w-4 h-4" /> Revoke
                    </button>
                  )}
                  {c.active ? (
                    <button
                      onClick={() => { if (window.confirm("Disable this account? They will be signed out and blocked from logging in.")) patch(c.id, { active: false }); }}
                      data-testid={`customer-disable-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-700 border border-red-200 px-3 py-2 rounded-sm hover:bg-red-50"
                    >
                      Disable
                    </button>
                  ) : (
                    <button
                      onClick={() => patch(c.id, { active: true })}
                      data-testid={`customer-enable-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-ei-navy border border-ei-mist px-3 py-2 rounded-sm hover:border-ei-navy"
                    >
                      Enable
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
